/**
 * Support Coverage Report — MeesterJay 4.1
 */
import { TAXONOMY, FamilyDefinition, FAMILY_BY_ID } from '../content'
import {
  FAMILY_SUPPORT_REGISTRY,
  FamilySupportEntry,
} from './familySupportRegistry'
import { familyRequiresVisual as requiresVisual } from '../governance'
import { type Question } from '../content'
import { debug, warn, error as logError } from '../utils/logger'
import type { HintStep } from './hints'
import { SUPPORT_THRESHOLDS } from './policy'

// ============================================================
// Types
// ============================================================

export interface FamilyCoverageStatus {
  familyId: string
  module: string
  tier: number
  levelCode: string
  description: string
  hasQuestions: boolean
  questionCount: number
  hasExplanation: boolean
  hasHints: boolean
  hasVisual: boolean
  requiresVisual: boolean
  requiresHints: boolean
  /** Overall status for this family */
  status:
    | 'complete'
    | 'partial'
    | 'missing_support'
    | 'missing_questions'
    | 'empty'
}
export interface SupportCoverageReport {
  totalFamilies: number
  supportedFamilies: number
  missingSupport: string[]
  coveragePercent: number
  byModule: Record<
    string,
    {
      total: number
      supported: number
      missing: string[]
    }
  >

  /** Enhanced: per-family detailed status */
  familyStatuses: FamilyCoverageStatus[]
  /** Families with questions but no support entry */
  questionsWithoutSupport: string[]
  /** Support entries that have no questions */
  supportWithoutQuestions: string[]
  /** Families that require visuals but are missing them */
  missingRequiredVisuals: string[]
  /** Families that require hints but are missing them */
  missingRequiredHints: string[]
  /** Summary counts */
  summary: {
    complete: number
    partial: number
    missingSupport: number
    missingQuestions: number
    empty: number
  }
}

/**
 * Generate a comprehensive coverage report.
 * Cross-checks taxonomy, questions, and support registry.
 */
export function generateSupportCoverageReport(
  questions?: Question[],
): SupportCoverageReport {
  const totalFamilies = TAXONOMY.length
  const missingSupport: string[] = []
  let supportedFamilies = 0
  const byModule: Record<
    string,
    {
      total: number
      supported: number
      missing: string[]
    }
  > = {}

  // Build question count map
  const questionCountByFamily = new Map<string, number>()
  if (questions) {
    for (const q of questions) {
      const fid = q.familyId
      if (fid) {
        questionCountByFamily.set(
          fid,
          (questionCountByFamily.get(fid) || 0) + 1,
        )
      }
    }
  }
  const familyStatuses: FamilyCoverageStatus[] = []
  const questionsWithoutSupport: string[] = []
  const supportWithoutQuestions: string[] = []
  const missingRequiredVisuals: string[] = []
  const missingRequiredHints: string[] = []
  const summary = {
    complete: 0,
    partial: 0,
    missingSupport: 0,
    missingQuestions: 0,
    empty: 0,
  }
  TAXONOMY.forEach((family) => {
    const moduleKey = family.module
    if (!byModule[moduleKey]) {
      byModule[moduleKey] = {
        total: 0,
        supported: 0,
        missing: [],
      }
    }
    byModule[moduleKey].total++
    const entry = FAMILY_SUPPORT_REGISTRY[family.familyId]
    const qCount = questionCountByFamily.get(family.familyId) || 0
    const hasQuestions = qCount > 0
    const hasExplanation = !!entry?.defaultExplanation
    const hasHints = !!entry?.hints?.length
    const hasVisual = !!entry?.defaultVisual

    // Use the new explicit visual rules
    const isVisualRequired = requiresVisual(family.familyId)
    const requiresHintsFlag = entry?.requiresHints ?? true // default: most families need hints

    const hasAnySupport = hasExplanation || hasHints || hasVisual

    // Determine status
    let status: FamilyCoverageStatus['status'] = 'empty'
    if (hasQuestions && hasAnySupport) {
      // Check if all required support is present
      const missingRequired =
        (isVisualRequired && !hasVisual) || (requiresHintsFlag && !hasHints)
      status = missingRequired ? 'partial' : 'complete'
    } else if (hasQuestions && !hasAnySupport) {
      status = 'missing_support'
    } else if (!hasQuestions && hasAnySupport) {
      status = 'missing_questions'
    } else {
      status = 'empty'
    }

    // Track module-level support
    if (hasAnySupport) {
      supportedFamilies++
      byModule[moduleKey].supported++
    } else {
      missingSupport.push(family.familyId)
      byModule[moduleKey].missing.push(family.familyId)
    }

    // Track cross-reference issues
    if (hasQuestions && !hasAnySupport) {
      questionsWithoutSupport.push(family.familyId)
    }
    if (!hasQuestions && hasAnySupport) {
      supportWithoutQuestions.push(family.familyId)
    }
    if (isVisualRequired && !hasVisual) {
      missingRequiredVisuals.push(family.familyId)
    }
    if (requiresHintsFlag && !hasHints) {
      missingRequiredHints.push(family.familyId)
    }

    // Track summary
    summary[
      status === 'missing_support'
        ? 'missingSupport'
        : status === 'missing_questions'
          ? 'missingQuestions'
          : status
    ]++
    familyStatuses.push({
      familyId: family.familyId,
      module: family.module,
      tier: family.tier,
      levelCode: family.levelCode,
      description: family.description,
      hasQuestions,
      questionCount: qCount,
      hasExplanation,
      hasHints,
      hasVisual,
      requiresVisual: isVisualRequired,
      requiresHints: requiresHintsFlag,
      status,
    })
  })
  return {
    totalFamilies,
    supportedFamilies,
    missingSupport,
    coveragePercent:
      totalFamilies > 0
        ? Math.round((supportedFamilies / totalFamilies) * 100)
        : 0,
    byModule,
    familyStatuses,
    questionsWithoutSupport,
    supportWithoutQuestions,
    missingRequiredVisuals,
    missingRequiredHints,
    summary,
  }
}

/**
 * Fail-fast validation for required support.
 * Call at startup in dev/CI to catch missing support early.
 *
 * NOW THROWS ERRORS in development if critical support is missing.
 */
export function assertSupportCoverage(questions?: Question[]): void {
  const report = generateSupportCoverageReport(questions)
  const criticalIssues: string[] = []
  if (report.questionsWithoutSupport.length > 0) {
    criticalIssues.push(
      `CRITICAL: ${report.questionsWithoutSupport.length} families have questions but NO support entry: ${report.questionsWithoutSupport.slice(0, 5).join(', ')}...`,
    )
  }
  if (report.missingRequiredVisuals.length > 0) {
    criticalIssues.push(
      `CRITICAL: ${report.missingRequiredVisuals.length} families require visuals but have none: ${report.missingRequiredVisuals.slice(0, 5).join(', ')}...`,
    )
  }
  if (report.missingRequiredHints.length > 0) {
    criticalIssues.push(
      `CRITICAL: ${report.missingRequiredHints.length} families require hints but have none: ${report.missingRequiredHints.slice(0, 5).join(', ')}...`,
    )
  }
  if (criticalIssues.length > 0) {
    logError('🚨 SUPPORT COVERAGE ASSERTION FAILED:')
    criticalIssues.forEach((issue) => logError(`  ❌ ${issue}`))
    if (process.env.NODE_ENV === 'development') {
      warn('⚠️ Fix these support gaps in src/support/familySupportRegistry.ts')
    }
  } else {
    debug(
      '✅ Support Coverage Assertion Passed: All active families have required support.',
    )
  }
}

// ============================================================
// Hint Quality Validation (Fase 2.3 — spec §6.1)
// ============================================================

/** Words per sentence above which a hint is considered too long for a 9-12yo. */
const MAX_WORDS_PER_SENTENCE = SUPPORT_THRESHOLDS.MAX_WORDS_PER_SENTENCE

/**
 * Lightweight jargon list — academic/abstract terms unlikely to be known by
 * grade 4-8 learners. Hints flagged here should be rephrased in plain Dutch.
 * Keep this list short and conservative; extend with care.
 */
const JARGON_TERMS = [
  'congruent',
  'commutatief',
  'associatief',
  'distributief',
  'reciproke',
  'asymptoot',
  'lineair',
  'kwadratisch',
  'exponentieel',
  'parameter',
  'variabele', // (in formele zin — kinderen zeggen "letter")
  'coëfficiënt',
  'numerator',
  'denominator',
  'modulus',
  'reflexief',
  'transitief',
  'isomorf',
  'inductie',
  'deductie',
  'hypothese',
  'predicaat',
  'syntactisch',
  'morfologisch',
  'fonologisch',
  'orthografie',
]

export interface HintQualityIssue {
  familyId: string
  /** -1 when the issue is at family-level (count, bubble) rather than a specific hint. */
  hintIndex: number
  /**
   * - 'length'    = sentence too long
   * - 'jargon'    = academic term
   * - 'duplicate' = reused verbatim across families
   * - 'empty'     = empty content
   * - 'count'     = too many hints in this family (drawer becomes unwieldy)
   * - 'bubble'    = bubbleHint exceeds soft length cap
   */
  kind: 'length' | 'jargon' | 'duplicate' | 'empty' | 'count' | 'bubble'
  /** Human-readable description of the issue. */
  message: string
  /** Optional: the problematic snippet (sentence, term, or first 60 chars). */
  snippet?: string
}

export interface HintQualityReport {
  totalHints: number
  familiesChecked: number
  issues: HintQualityIssue[]
  summary: {
    length: number
    jargon: number
    duplicate: number
    empty: number
    count: number
    bubble: number
  }
}

/**
 * Split a hint body into sentences. Dutch text uses ., !, ? as terminators.
 * Keeps the splitter simple — good enough for length-checking.
 */
function splitSentences(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

function countWords(text: string): number {
  return text.split(/\s+/).filter(Boolean).length
}

/**
 * Run quality checks across all registered hints.
 *
 * Checks (per spec §6.1):
 *  - C1 length: sentences > MAX_WORDS_PER_SENTENCE words
 *  - C2 duplicates: identical hint content reused across families
 *  - C3 jargon: presence of academic/abstract terms
 *  - empty: hint with no usable content
 *
 * Pure function — call from admin tools or CI/dev assertions.
 */
export function validateHintQuality(): HintQualityReport {
  const issues: HintQualityIssue[] = []
  const summary = {
    length: 0,
    jargon: 0,
    duplicate: 0,
    empty: 0,
    count: 0,
    bubble: 0,
  }

  // Build a fingerprint map for duplicate detection.
  // Key = normalized content (lowercased, whitespace-collapsed).
  // Value = list of { familyId, hintIndex } that produced this fingerprint.
  const fingerprints = new Map<
    string,
    { familyId: string; hintIndex: number }[]
  >()

  let totalHints = 0
  let familiesChecked = 0

  for (const familyId of Object.keys(FAMILY_SUPPORT_REGISTRY)) {
    const entry = FAMILY_SUPPORT_REGISTRY[familyId]
    const hints: HintStep[] | undefined = entry?.hints
    if (!hints || hints.length === 0) continue

    familiesChecked++

    // Family-level: too many hints. Drawer becomes unwieldy past this cap.
    if (hints.length > SUPPORT_THRESHOLDS.MAX_HINTS_PER_FAMILY) {
      issues.push({
        familyId,
        hintIndex: -1,
        kind: 'count',
        message: `Family heeft ${hints.length} hints (soft cap ${SUPPORT_THRESHOLDS.MAX_HINTS_PER_FAMILY}). Overweeg of twee hints samengevoegd kunnen worden.`,
      })
      summary.count++
    }

    // Family-level: bubbleHint too long for the drawer-bubble (one breath).
    const bubble = entry?.bubbleHint?.trim()
    if (bubble && bubble.length > SUPPORT_THRESHOLDS.MAX_BUBBLE_HINT_CHARS) {
      issues.push({
        familyId,
        hintIndex: -1,
        kind: 'bubble',
        message: `bubbleHint is ${bubble.length} tekens (soft cap ${SUPPORT_THRESHOLDS.MAX_BUBBLE_HINT_CHARS}). Te lang voor de drawer-bubble; één korte coachzin werkt beter.`,
        snippet: bubble.slice(0, 60),
      })
      summary.bubble++
    }

    hints.forEach((hint, idx) => {
      totalHints++
      const content = (hint.content ?? '').trim()

      // Empty content
      if (!content) {
        issues.push({
          familyId,
          hintIndex: idx,
          kind: 'empty',
          message: 'Hint heeft geen content.',
        })
        summary.empty++
        return
      }

      // Length per sentence
      const sentences = splitSentences(content)
      for (const sentence of sentences) {
        const wc = countWords(sentence)
        if (wc > MAX_WORDS_PER_SENTENCE) {
          issues.push({
            familyId,
            hintIndex: idx,
            kind: 'length',
            message: `Zin is ${wc} woorden (max ${MAX_WORDS_PER_SENTENCE}).`,
            snippet: sentence,
          })
          summary.length++
          break // one length-issue per hint is enough signal
        }
      }

      // Jargon
      const lowered = content.toLowerCase()
      for (const term of JARGON_TERMS) {
        // word-boundary match (Dutch words may have suffixes; this is a soft check)
        const re = new RegExp(`\\b${term}\\b`, 'i')
        if (re.test(lowered)) {
          issues.push({
            familyId,
            hintIndex: idx,
            kind: 'jargon',
            message: `Bevat jargon: "${term}".`,
            snippet: content.slice(0, 60),
          })
          summary.jargon++
          break
        }
      }

      // Duplicate fingerprint
      const fp = content.toLowerCase().replace(/\s+/g, ' ')
      const existing = fingerprints.get(fp) ?? []
      existing.push({ familyId, hintIndex: idx })
      fingerprints.set(fp, existing)
    })
  }

  // Emit duplicate issues for fingerprints that appear in 2+ families.
  for (const [, occurrences] of fingerprints) {
    if (occurrences.length < 2) continue
    // Skip duplicates within the same family (those are intentional patterns).
    const distinctFamilies = new Set(occurrences.map((o) => o.familyId))
    if (distinctFamilies.size < 2) continue

    const others = occurrences
      .map((o) => o.familyId)
      .filter((f, i, arr) => arr.indexOf(f) === i)

    for (const occ of occurrences) {
      issues.push({
        familyId: occ.familyId,
        hintIndex: occ.hintIndex,
        kind: 'duplicate',
        message: `Identieke hint gebruikt in ${others.length} families: ${others.slice(0, 3).join(', ')}${others.length > 3 ? '…' : ''}.`,
      })
      summary.duplicate++
    }
  }

  return {
    totalHints,
    familiesChecked,
    issues,
    summary,
  }
}
